import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react'
import { MultiSelect } from 'chakra-multiselect'
import { map } from 'lodash'
import BasicMonthPicker from '../../../components/BasicMonthPicker'
import CountrySelect from '../../../components/CountrySelect'
import { diagnosisTitles } from '../../../types/FirestoreUser'
import useProgressionQueryForm from '../../../hooks/useProgressionQueryForm'
import { ProgressionQueryParams } from '../../../types/ProgressionQuery'

interface Props {
  onSubmit: (params: ProgressionQueryParams) => Promise<void>
  isLoading: boolean
}

export default function QueryForm({ onSubmit, isLoading }: Props) {
  const { formState, updateField, validate, buildParams } =
    useProgressionQueryForm()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    const params = buildParams()
    await onSubmit(params)
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={4}>
        <FormControl isRequired>
          <FormLabel>Find me users whose following score...</FormLabel>
          <RadioGroup
            value={formState.type}
            onChange={(v) => updateField('type', v)}
          >
            <HStack spacing={4}>
              <Radio value='alsfrs'>ALSFRS</Radio>
              <Radio value='alssq'>ALSSQ</Radio>
              <Radio value='combined'>Combined</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Has gone in the following direction...</FormLabel>
          <RadioGroup
            value={formState.direction}
            onChange={(v) => updateField('direction', v)}
          >
            <HStack spacing={4}>
              <Radio value='up'>Up</Radio>
              <Radio value='down'>Down</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
        <FormControl isRequired>
          <FormLabel>By at least...</FormLabel>
          <Input
            value={formState.minChange}
            onChange={(e) => updateField('minChange', e.target.value)}
            type='number'
            placeholder='Minimum score change'
            step='1'
          />
        </FormControl>
        <SimpleGrid columns={[1, 2]} spacing={4}>
          <FormControl isRequired>
            <FormLabel>From (Year and Month)</FormLabel>
            <BasicMonthPicker
              startYear='2018'
              endYear={new Date().getFullYear().toString()}
              dateString={formState.fromDate}
              setDateString={(v: string) => updateField('fromDate', v)}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>To (Year and Month)</FormLabel>
            <BasicMonthPicker
              startYear='2018'
              endYear={new Date().getFullYear().toString()}
              dateString={formState.toDate}
              setDateString={(v: string) => updateField('toDate', v)}
            />
          </FormControl>
        </SimpleGrid>
        <QueryFormFilters formState={formState} updateField={updateField} />
        <Box>
          <Button
            size='lg'
            type='submit'
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            Submit
          </Button>
        </Box>
      </Stack>
    </form>
  )
}

// Extract filters to separate component to reduce main form size
interface FiltersProps {
  formState: ReturnType<typeof useProgressionQueryForm>['formState']
  updateField: ReturnType<typeof useProgressionQueryForm>['updateField']
}

function QueryFormFilters({ formState, updateField }: FiltersProps) {
  return (
    <SimpleGrid columns={[1, 2]} spacing={4}>
      <FormControl>
        <FormLabel>Min. Age</FormLabel>
        <Input
          type='number'
          step='1'
          value={formState.minAge}
          onChange={(e) => updateField('minAge', e.target.value)}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Max. Age</FormLabel>
        <Input
          type='number'
          step='1'
          value={formState.maxAge}
          onChange={(e) => updateField('maxAge', e.target.value)}
        />
      </FormControl>
      <CountrySelect
        defaultValue={formState.country}
        onChange={(v) => updateField('country', v)}
      />
      <FormControl>
        <FormLabel>Gender</FormLabel>
        <Select
          value={formState.gender}
          placeholder='Choose Gender'
          onChange={(e) => updateField('gender', e.target.value)}
        >
          <option value='male'>Male</option>
          <option value='female'>Female</option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Diagnosis</FormLabel>
        <MultiSelect
          options={map(diagnosisTitles, (label, value) => ({
            label,
            value,
          }))}
          value={formState.diagnosis}
          placeholder='Choose Diagnosis'
          onChange={(v) => updateField('diagnosis', v as any)}
        />
      </FormControl>
      <FormControl>
        <FormLabel># Months since Diagnosis</FormLabel>
        <Input
          type='number'
          step='1'
          value={formState.monthsSinceDiagnosis}
          onChange={(e) => updateField('monthsSinceDiagnosis', e.target.value)}
        />
      </FormControl>
    </SimpleGrid>
  )
}
